// src/pages/admin/AuditLogs.tsx - IMPROVED WITH TIMELINE VIEW
import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Search, Download, Eye, User, FileText,
  Settings, Shield, RefreshCw, Database, Calendar, Filter,
  List, X,
  LogIn, LogOut, Edit, Trash2, CheckCircle, XCircle, Plus,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select, Input, Pagination, Modal, Loading, LogoLoadingMark } from '../../components/ui';
import { adminService, ActivityLog, LogFilter } from '../../services/admin';
import toast from 'react-hot-toast';

type ViewMode = 'list' | 'timeline';

const eventConfig: Record<string, {
  bg: string;
  text: string;
  border: string;
  label: string;
  icon: React.ReactNode;
  gradient: string;
}> = {
  created: {
    bg: 'bg-success/10',
    text: 'text-success',
    border: 'border-success/30',
    label: 'Dibuat',
    icon: <Plus className="w-3.5 h-3.5" />,
    gradient: 'from-success to-emerald-600',
  },
  updated: {
    bg: 'bg-info/10',
    text: 'text-info',
    border: 'border-info/30',
    label: 'Diubah',
    icon: <Edit className="w-3.5 h-3.5" />,
    gradient: 'from-info to-blue-600',
  },
  deleted: {
    bg: 'bg-danger/10',
    text: 'text-danger',
    border: 'border-danger/30',
    label: 'Dihapus',
    icon: <Trash2 className="w-3.5 h-3.5" />,
    gradient: 'from-danger to-red-600',
  },
  validated: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    label: 'Validasi',
    icon: <CheckCircle className="w-3.5 h-3.5" />,
    gradient: 'from-purple-500 to-purple-600',
  },
  rejected: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-200',
    label: 'Ditolak',
    icon: <XCircle className="w-3.5 h-3.5" />,
    gradient: 'from-rose-500 to-rose-600',
  },
  login: {
    bg: 'bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-200',
    label: 'Login',
    icon: <LogIn className="w-3.5 h-3.5" />,
    gradient: 'from-teal-500 to-teal-600',
  },
  logout: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
    label: 'Logout',
    icon: <LogOut className="w-3.5 h-3.5" />,
    gradient: 'from-gray-500 to-gray-600',
  },
  default: {
    bg: 'bg-neutral-bg',
    text: 'text-neutral-text-secondary',
    border: 'border-neutral-border',
    label: 'Log',
    icon: <Activity className="w-3.5 h-3.5" />,
    gradient: 'from-gray-400 to-gray-500',
  },
};

const subjectIcons: Record<string, React.ReactNode> = {
  'LaporanMasalah': <FileText className="w-4 h-4" />,
  'User': <User className="w-4 h-4" />,
  'ValidasiLog': <Shield className="w-4 h-4" />,
  'SystemSetting': <Settings className="w-4 h-4" />,
  'Role': <Shield className="w-4 h-4" />,
  'Unit': <Database className="w-4 h-4" />,
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    today: 0,
    created: 0,
    updated: 0,
    deleted: 0,
  });

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const filters: LogFilter = {
        page: currentPage,
        per_page: 15,
        search: debouncedSearch,
        log_name: eventFilter,
        date_from: dateFrom,
        date_to: dateTo,
      };

      const res = await adminService.logs.getAll(filters);
      setLogs(res.data);
      setTotalPages(res.meta.last_page);

      // Calculate stats
      const today = new Date().toDateString();
      const todayLogs = res.data.filter(l => new Date(l.created_at).toDateString() === today);
      setStats({
        today: todayLogs.length,
        created: res.data.filter(l => l.log_name === 'created').length,
        updated: res.data.filter(l => l.log_name === 'updated').length,
        deleted: res.data.filter(l => l.log_name === 'deleted').length,
      });
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Gagal memuat log aktivitas');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, eventFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      const filters: LogFilter = {
        search: debouncedSearch,
        log_name: eventFilter,
        date_from: dateFrom,
        date_to: dateTo,
      };
      const arrayBuffer = await adminService.logs.export(filters);
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export berhasil!');
    } catch (e) {
      toast.error('Gagal mengexport log');
    }
  };

  const getEventConfig = (eventName: string) => {
    return eventConfig[eventName] || eventConfig.default;
  };

  const getSubjectIcon = (subjectType: string) => {
    const shortName = subjectType.split('\\').pop() || '';
    return subjectIcons[shortName] || <Activity className="w-4 h-4" />;
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays < 7) return `${diffDays} hari lalu`;
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const groupLogsByDate = (logs: ActivityLog[]) => {
    const groups: Record<string, ActivityLog[]> = {};

    logs.forEach(log => {
      const date = new Date(log.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let key: string;
      if (date.toDateString() === today.toDateString()) {
        key = 'Hari Ini';
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = 'Kemarin';
      } else {
        key = date.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(log);
    });

    return groups;
  };

  const clearFilters = () => {
    setSearch('');
    setEventFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || eventFilter || dateFrom || dateTo;

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Log Aktivitas' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-rose-500" />
            Log Aktivitas
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Riwayat semua aktivitas pengguna dalam sistem
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={loading ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
            onClick={fetchLogs}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<Download className="w-4 h-4" />}
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="!p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center shadow-sm">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-blue-600">{stats.today}</p>
              <p className="text-[10px] text-blue-600/70 font-semibold uppercase">Hari Ini</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shadow-sm">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-green-600">{stats.created}</p>
              <p className="text-[10px] text-green-600/70 font-semibold uppercase">Dibuat</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-sm">
              <Edit className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-amber-600">{stats.updated}</p>
              <p className="text-[10px] text-amber-600/70 font-semibold uppercase">Diubah</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center shadow-sm">
              <Trash2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-red-600">{stats.deleted}</p>
              <p className="text-[10px] text-red-600/70 font-semibold uppercase">Dihapus</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col gap-4">
          {/* Main Search Row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari aktivitas, user, atau deskripsi..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-main/20 focus:border-teal-main transition-all"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 p-1 bg-neutral-bg rounded-xl">
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${viewMode === 'timeline'
                    ? 'bg-white text-teal-main shadow-sm'
                    : 'text-neutral-text-secondary hover:text-neutral-text-primary'
                  }`}
              >
                <Activity className="w-4 h-4" />
                Timeline
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${viewMode === 'list'
                    ? 'bg-white text-teal-main shadow-sm'
                    : 'text-neutral-text-secondary hover:text-neutral-text-primary'
                  }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant={showFilters ? 'primary' : 'outline'}
              size="sm"
              icon={<Filter className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter
              {hasActiveFilters && (
                <span className="ml-1 w-2 h-2 bg-danger rounded-full" />
              )}
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-3 border-t border-neutral-border animate-in">
              <Select
                options={[
                  { value: '', label: 'Semua Event' },
                  { value: 'created', label: '✅ Dibuat' },
                  { value: 'updated', label: '📝 Diubah' },
                  { value: 'deleted', label: '🗑️ Dihapus' },
                  { value: 'validated', label: '✓ Validasi' },
                  { value: 'login', label: '🔐 Login' },
                  { value: 'logout', label: '🚪 Logout' },
                ]}
                placeholder="Tipe Event"
                value={eventFilter}
                onChange={setEventFilter}
              />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="Dari Tanggal"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="Sampai Tanggal"
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Log Content */}
      <Card padding="sm">
        {loading ? (
          <Loading variant="inline" message="Memuat log aktivitas..." className="py-8" />
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <Activity className="w-12 h-12 mx-auto mb-3 text-neutral-text-secondary opacity-30" />
            <p className="text-base font-bold text-neutral-text-primary">Tidak ada log ditemukan</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {hasActiveFilters ? 'Coba ubah filter pencarian' : 'Belum ada aktivitas tercatat'}
            </p>
          </div>
        ) : viewMode === 'timeline' ? (
          /* TIMELINE VIEW */
          <div className="relative">
            {Object.entries(groupLogsByDate(logs)).map(([dateGroup, groupLogs], groupIdx) => (
              <div key={dateGroup} className="mb-8 last:mb-0">
                {/* Date Header */}
                <div className="sticky top-0 z-10 mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-main text-white text-xs font-bold rounded-full shadow-sm">
                    <Calendar className="w-3.5 h-3.5" />
                    {dateGroup}
                  </span>
                </div>

                {/* Timeline Items */}
                <div className="relative ml-4">
                  {/* Timeline Line */}
                  <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-main via-teal-light to-transparent" />

                  <div className="space-y-4">
                    {groupLogs.map((log, idx) => {
                      const config = getEventConfig(log.log_name);
                      const isFirst = idx === 0 && groupIdx === 0;

                      return (
                        <div
                          key={log.id}
                          onClick={() => setSelectedLog(log)}
                          className="relative flex gap-4 pl-6 cursor-pointer group"
                        >
                          {/* Timeline Dot */}
                          <div className={`
                            absolute left-0 w-6 h-6 rounded-full flex items-center justify-center
                            bg-gradient-to-br ${config.gradient} text-white shadow-md
                            ring-4 ring-white group-hover:scale-110 transition-transform
                            ${isFirst ? 'ring-teal-100' : ''}
                          `}>
                            {config.icon}
                          </div>

                          {/* Content Card */}
                          <div className="flex-1 p-4 bg-white rounded-xl border border-neutral-border/50 hover:border-teal-main/30 hover:shadow-md transition-all group">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${config.bg} ${config.text} ${config.border}`}>
                                    {config.icon}
                                    {config.label}
                                  </span>
                                  <span className="text-[10px] text-neutral-text-secondary">
                                    {log.subject_type?.split('\\').pop()}
                                  </span>
                                </div>

                                <p className="text-sm font-medium text-neutral-text-primary group-hover:text-teal-dark transition-colors">
                                  {log.description}
                                </p>

                                <div className="flex items-center gap-3 mt-2 text-xs text-neutral-text-secondary">
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <strong>{log.causer?.name || 'System'}</strong>
                                  </span>
                                  {log.causer?.role && (
                                    <span className="px-1.5 py-0.5 bg-neutral-bg rounded text-[10px]">
                                      {(log.causer as any).role?.name || ''}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="text-right shrink-0">
                                <p className="text-xs font-medium text-neutral-text-secondary">
                                  {new Date(log.created_at).toLocaleTimeString('id-ID', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                                <p className="text-[10px] text-neutral-text-secondary mt-0.5">
                                  {formatRelativeTime(log.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* LIST VIEW */
          <div className="space-y-1">
            {logs.map((log) => {
              const config = getEventConfig(log.log_name);
              return (
                <button
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-neutral-bg/80 transition-colors text-left"
                >
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold ${config.bg} ${config.text} shrink-0`}>
                    {config.icon}
                    {config.label}
                  </span>

                  <div className="p-2 bg-neutral-bg rounded-lg shrink-0 text-neutral-text-secondary">
                    {getSubjectIcon(log.subject_type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-text-primary truncate">
                      {log.description}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      <span className="font-semibold">{log.causer?.name || 'System'}</span>
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">
                      {new Date(log.created_at).toLocaleDateString('id-ID')}
                    </p>
                    <p className="text-xs text-neutral-text-secondary">
                      {new Date(log.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>

                  <Eye className="w-4 h-4 text-neutral-text-secondary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {logs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-neutral-border">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="📋 Detail Log Aktivitas"
        size="lg"
      >
        {selectedLog && (
          <div className="space-y-4">
            {/* Event Badge */}
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getEventConfig(selectedLog.log_name).gradient} flex items-center justify-center text-white shadow-lg`}>
                {getEventConfig(selectedLog.log_name).icon}
              </div>
              <div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getEventConfig(selectedLog.log_name).bg} ${getEventConfig(selectedLog.log_name).text}`}>
                  {getEventConfig(selectedLog.log_name).label}
                </span>
                <p className="text-sm text-neutral-text-secondary mt-1">
                  {selectedLog.subject_type?.split('\\').pop()} #{selectedLog.subject_id}
                </p>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-neutral-bg rounded-xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-1">User</p>
                <p className="text-sm font-semibold">{selectedLog.causer?.name || 'System'}</p>
                {selectedLog.causer?.role && (
                  <p className="text-xs text-neutral-text-secondary">{(selectedLog.causer as any).role?.name}</p>
                )}
              </div>
              <div className="p-3 bg-neutral-bg rounded-xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-1">Waktu</p>
                <p className="text-sm font-semibold">
                  {new Date(selectedLog.created_at).toLocaleString('id-ID')}
                </p>
                <p className="text-xs text-neutral-text-secondary">
                  {formatRelativeTime(selectedLog.created_at)}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="p-4 bg-neutral-bg rounded-xl">
              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-2">Deskripsi</p>
              <p className="text-sm text-neutral-text-primary">{selectedLog.description}</p>
            </div>

            {/* Properties */}
            {selectedLog.properties && Object.keys(selectedLog.properties).length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-2">
                  Data Perubahan
                </p>
                <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-xl overflow-x-auto font-mono">
                  {JSON.stringify(selectedLog.properties, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-neutral-border">
              <Button variant="outline" onClick={() => setSelectedLog(null)}>
                Tutup
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogs;
